import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { trackEvent } from 'analytics/trackFunctions'
import Skeleton from 'react-loading-skeleton'
import { useSelectedAccountStore, useSelectedContactStore } from 'store'
import { toTitleCase, formatCUIL } from 'utils'
import { getContact } from 'api/bank'
import PageWrapper from 'components/PageWrapper'
import RoundedItem from 'components/RoundedItem'
import Card from 'components/Card'
import { MeContext } from 'contexts/me'
import { BankDefault } from 'assets/icons'

const Contact = () => {
  const [contactData, setContactData] = useState(null)
  const { selectedContact } = useSelectedContactStore()
  const { updateEntityData, updateSelectedAccount } = useSelectedAccountStore()
  const { me } = useContext(MeContext)
  const navigate = useNavigate()
  const params = useParams()

  useEffect(() => {
    trackEvent({ eventName: 'bille_pantalla_cuentas_asociadas' })
  }, [])

  useEffect(() => {
    if (!me) return
    if (contactData) return

    if (params.own === 'mis-cuentas') {
      const contact = {
        name: toTitleCase(`${me.profile.name} ${me.profile.surname}`),
        taxID: formatCUIL(me.profile.tax_identification_value),
        accounts: me.bank.external_account
      }

      setContactData(contact)
    } else if (selectedContact) {
      getContact(selectedContact).then((res) => {
        if (res.success) {
          const { result: data } = res.data
          if (data.length === 0) return navigate('/error', { replace: true })

          const taxValue = Object.values(data)[0][0].tax_identification_value

          const contact = {
            name: toTitleCase(Object.keys(data)[0]),
            taxID: formatCUIL(taxValue.toString()),
            accounts: Object.values(data)[0]
          }

          setContactData(contact)
        }
      })
    }
  }, [me])

  const handleButton = (account) => {
    updateEntityData({
      bankName: account.bank_name,
      iconURL: account.bank_image
    })

    if (params.own === 'mis-cuentas') {
      updateSelectedAccount({
        CVU: account.cvu,
        CBU: account.cbu,
        CUIL: me?.profile?.tax_identification_value,
        accountHolder: toTitleCase(`${me.profile.name} ${me.profile.surname}`),
        comesFromNewAccount: false
      })
    } else {
      updateSelectedAccount({
        CVU: account.cvu,
        CBU: account.cbu,
        CUIL: account.tax_identification_value,
        accountHolder: account.account_holder,
        comesFromNewAccount: false
      })
    }

    navigate('/transferencias/destinatario')
  }

  return (
    <PageWrapper noTabBar title='Transferir dinero' viewSelected='/movimientos'>
      <section className='flex flex-col text-black mx-auto sm:max-w-[25rem] h-[calc(100vh-12rem)]'>
        <p className='text-xs font-medium text-left'>
          Seleccioná la cuenta para transferir
        </p>
        {contactData ? (
          <>
            <div className='my-5 flex gap-3 items-center text-left p-3 text-xs font-medium'>
              <RoundedItem
                roundedIconClassName='!h-9 !w-9'
                hasTitle={false}
                name={contactData.name}
              />
              <div>
                <p className='font-bold'>{contactData.name}</p>
                <p className='text-gray-900'>CUIL: {contactData.taxID}</p>
              </div>
            </div>
            <div>
              {contactData.accounts.map((a) => (
                <AccountCard
                  key={a.cbu || a.cvu}
                  onClick={() => handleButton(a)}
                  className='mb-2'
                  bankName={a.bank_name}
                  bankImage={a.bank_image}
                  value={a.cvu || a.cbu}
                />
              ))}
            </div>
          </>
        ) : (
          <div className='w-full'>
            <Skeleton height={66} className='my-5' />
            <Skeleton height={66} count={2} className='mb-2' />
          </div>
        )}
      </section>
    </PageWrapper>
  )
}

export default Contact

interface AccountCardProps {
  bankName: string
  bankImage?: string | null
  value: string
  className?: string
  onClick: () => void
}

const AccountCard = ({
  bankName = '',
  bankImage = null,
  value = '',
  className = '',
  onClick = () => null
}: AccountCardProps) => (
  <button onClick={() => onClick()} className='w-full'>
    <Card className={`bg-white items-center flex p-3 gap-3 ${className}`}>
      {bankImage ? (
        <img
          key={bankImage}
          className='rounded-full h-9 w-9'
          src={bankImage}
          alt=''
        />
      ) : (
        <BankDefault />
      )}
      <div className='text-xs text-left text-black font-medium'>
        <p className='font-bold'>{bankName}</p>
        <p>CBU/CVU: {value}</p>
      </div>
    </Card>
  </button>
)
